/**
 * <sunday-never>: Never compressed, look "drawn" (31 Oct 2026, AR approved, after v17).
 * Two rooms drawn once in flat hero style (wall and floor only; the sleeper in a navy top with a cream edge and a white
 * spine line reads first). The Sunday room never moves. In the other room five elephants press the
 * mattress from full height to 1 inch ("20,000 kg" tag), it rolls up, unrolls, and goes soft under the sleeper: a red
 * marker pulses at the hips and her spine bends. Plays once when half the room is on screen (reduced motion too: story
 * animations play), and again on the button or a tap on either room. One rAF loop, only while playing and on screen.
 * Each frame changes attributes only; nothing is rebuilt. Words: data-sunday-text "never" (snippets/sunday-js-text).
 */
import { t } from '@sunday/text';

const LN = '#2A3341', HARM = '#C8551F', CALM = '#FFFFFF', SKIN = '#C98E5B', HAIR = '#2A1C12', SHEET = '#FFFFFF', EDGE = '#F6EEDC', KURTA = '#2A3341';
/* Sunday mattress: cream top + band (page colour, sage default). Compressed: one flat sand colour + a thin line that shows the sag.
   Outlines about 2 px on screen. */
const VIVID = { sw: 2.2, wash: 0, wall: '#FFF8EA', floor: '#E9DDBF', wood: '#E9DDBF', top: '#F1E6C4', low: '#5E8A78', seam: 0 };
const TIRED = { sw: 1.8, wash: .1, wall: '#EEDDC2', floor: '#DCC6A0', wood: '#E0CBA6', top: '#E8D8B6', low: '#E8D8B6', seam: 1 };
const NSS = 'vector-effect="non-scaling-stroke"';
const sw = (S) => 'stroke="' + LN + '" stroke-width="' + S.sw + '" stroke-linejoin="round" stroke-linecap="round" ' + NSS;
/* Bed space: mattress X0..X1, floor of the mattress B, full height H. */
const X0 = 24, X1 = 346, B = 104, H = 38;
const f = (n) => Math.round(n * 10) / 10;
const BED = 'translate(2 35.2) scale(.78)'; /* phone default; fitLabels() places the bed per screen */
const FONT = 'font-family="system-ui,-apple-system,Segoe UI,Roboto,sans-serif" font-weight="700"';

/* Hips sink most, shoulders too, waist less: the spine bends. */
const dip = (x, d) => d * (Math.exp(-Math.pow((x - 208) / 46, 2)) + 0.62 * Math.exp(-Math.pow((x - 116) / 26, 2)));
const pts = (a, b, step, fy) => { let s = ''; for (let x = a; b > a ? x <= b : x >= b; x += step) s += (s ? 'L' : 'M') + f(x) + ' ' + f(fy(x)); return s; };

/* The room is only wall and floor: nothing behind her competes with the sleeper. */
function room(S) {
  let s = '<rect x="0" y="0" width="360" height="130" fill="' + S.wall + '"/>';
  if (S.wash) s += '<rect x="0" y="0" width="360" height="130" fill="#8A5A2B" opacity="' + S.wash + '"/>';
  return s + '<rect x="0" y="121" width="360" height="12" fill="' + S.floor + '"/><path d="M0 121H360" ' + sw(S) + '/>';
}
const bed = (S) => '<path d="M10 110V60q0-8 8-8h7v58z" fill="' + S.wood + '" ' + sw(S) + '/><rect x="16" y="' + B + '" width="338" height="4.5" rx="1.5" fill="' + LN + '"/>';

/* Mattress geometry: outline, lower band and seam follow any dip. */
function mattGeo(h, d) {
  const T = B - h, r = Math.min(6, h / 2), lb = B - h * 0.46;
  let edge = '';
  for (let x = X0 + r; x <= X1 - r; x += 4) edge += 'L' + f(x) + ' ' + f(T + dip(x, d));
  const base = 'V' + f(B - r) + 'Q' + X1 + ' ' + B + ' ' + f(X1 - r) + ' ' + B + 'H' + f(X0 + r) + 'Q' + X0 + ' ' + B + ' ' + X0 + ' ' + f(B - r);
  const o = 'M' + X0 + ' ' + f(B - r) + 'V' + f(T + r) + 'Q' + X0 + ' ' + f(T) + ' ' + f(X0 + r) + ' ' + f(T) + edge +
    'L' + f(X1 - r) + ' ' + f(T) + 'Q' + X1 + ' ' + f(T) + ' ' + X1 + ' ' + f(T + r) + base + 'Z';
  if (h <= 8) return { o, l: '', s: '' };
  const seam = pts(X0, X1, 4, (x) => lb + dip(x, d) * 0.45);
  return { o, l: seam + 'L' + X1 + ' ' + f(lb) + base + 'Z', s: seam };
}
function mattress(S, h, d) {
  const g = mattGeo(h, d);
  return '<path data-k="mf" d="' + g.o + '" fill="' + S.top + '"/><path data-k="ml" d="' + g.l + '" fill="' + S.low + '"/>' +
    '<path data-k="ms" d="' + g.s + '" fill="none" stroke="#A8875A" stroke-opacity="' + S.seam + '" stroke-width="1.5" ' + NSS + '/>' +
    '<path data-k="mo" d="' + g.o + '" fill="none" ' + sw(S) + '/>';
}

/* The sleeper: on her back, head on the pillow, kurta, arm along her side, duvet over the legs. Her back rests on the
   mattress top, so where it dips she sinks in. The spine line is straight on a firm mattress and bends where it sags. */
const lerpT = (x, a, b, ya, yz) => ya + (yz - ya) * Math.min(1, Math.max(0, (x - a) / (b - a)));
const TH = (x) => x < 92 ? lerpT(x, 72, 92, 13, 24) : x < 134 ? 24 + Math.sin((x - 92) / 42 * Math.PI) * 2.5 : x < 170 ? lerpT(x, 134, 170, 24, 19)
  : x < 234 ? 19 + Math.sin((x - 170) / 64 * Math.PI) * 6.5 : lerpT(x, 234, 244, 19, 16.5);
const LEG = (x) => x < 300 ? 16.5 + Math.max(0, 1 - Math.abs(x - 268) / 30) * 5 - (x - 236) * 0.03 : x < 320 ? 14.5 : 14.5 + Math.max(0, 1 - Math.abs(x - 331) / 9) * 12;
function figGeo(h, d, dy, p) {
  const T = B - h, yb = (x) => T + dip(x, d) + dy, top = (x) => yb(x) - TH(x);
  const pt = T + dy - 15 + dip(56, d) * .5, cx = 54, r = 13, cy = pt - 4;
  const g = { pt: f(pt), cy: f(cy) };
  g.torso = pts(72, 246, 4, top) + pts(246, 72, -4, yb).replace('M', 'L') + 'Z';
  g.spine = pts(80, 214, 3, (x) => yb(x) - 8);
  g.arm = 'M93 ' + f(top(93) + 8) + 'Q128 ' + f(top(128) + 13) + ' 160 ' + f(top(160) + 6);
  g.hand = f(top(167) + 6);
  g.duvet = pts(234, 340, 4, (x) => yb(x) - LEG(x) - 2.5) + pts(340, 234, -4, (x) => yb(x) + .5).replace('M', 'L') + 'Z';
  g.hem = 'M240 ' + f(yb(240) - LEG(240) - 2) + 'L240 ' + f(yb(240));
  g.neck = 'M' + (cx + 7) + ' ' + f(cy + 5) + 'L77 ' + f(yb(77) - 7);
  g.nose = 'M' + f(cx + 3) + ' ' + f(cy - r + .4) + 'q2.6 -3.4 4.8 -.2';
  g.eye = 'M' + f(cx - 2.5) + ' ' + f(cy - 7) + 'q2.7 2 5.4 0';
  g.hair = 'M' + f(cx - 1) + ' ' + f(cy - r) + 'A' + r + ' ' + r + ' 0 0 0 ' + f(cx - 4) + ' ' + f(cy + r - .5) + 'Q' + f(cx - 6.5) + ' ' + f(cy + 2) + ' ' + f(cx - 6.5) + ' ' + f(cy - 3) + 'Q' + f(cx - 5.5) + ' ' + f(cy - 10) + ' ' + f(cx - 1) + ' ' + f(cy - r) + 'Z';
  g.heat = { cy: f(T + dip(206, d) + 3), rx: f(48 * (1 + .16 * p)), ry: f(10 * (1 + .22 * p)) };
  return g;
}
function sleeper(S, id, h, spine) {
  const g = figGeo(h, 0, 0, 0), W = sw(S), e = g.heat;
  return '<ellipse data-k="heat" cx="206" cy="' + e.cy + '" rx="' + e.rx + '" ry="' + e.ry + '" fill="url(#' + id + 'h)" opacity="0"/>' +
    '<g data-k="fig">' +
    '<rect data-k="pil" x="28" y="' + g.pt + '" width="58" height="16" rx="8" fill="#FFFFFF" ' + W + '/>' +
    '<path data-k="neck" d="' + g.neck + '" fill="none" stroke="' + LN + '" stroke-width="' + (S.sw * 2 + 10) + '" stroke-linecap="round" ' + NSS + '/>' +
    '<path data-k="neck" d="' + g.neck + '" fill="none" stroke="' + SKIN + '" stroke-width="10" stroke-linecap="round" ' + NSS + '/>' +
    '<path data-k="torso" d="' + g.torso + '" fill="' + KURTA + '" ' + W + '/>' +
    '<path data-k="torso" d="' + g.torso + '" fill="none" stroke="' + EDGE + '" stroke-width="1" stroke-linejoin="round" ' + NSS + '/>' +
    '<path data-k="spine" d="' + g.spine + '" fill="none" stroke="' + spine + '" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" ' + NSS + '/>' +
    '<path data-k="arm" d="' + g.arm + '" fill="none" stroke="' + EDGE + '" stroke-width="11" stroke-linecap="round" ' + NSS + '/>' +
    '<path data-k="arm" d="' + g.arm + '" fill="none" stroke="' + KURTA + '" stroke-width="9" stroke-linecap="round" ' + NSS + '/>' +
    '<ellipse data-k="hand" cx="167" cy="' + g.hand + '" rx="7" ry="5" fill="' + SKIN + '" ' + W + '/>' +
    '<path data-k="duvet" d="' + g.duvet + '" fill="' + SHEET + '" ' + W + '/>' +
    '<path data-k="hem" d="' + g.hem + '" stroke="' + LN + '" stroke-opacity=".35" stroke-width="1.5" ' + NSS + '/>' +
    '<circle data-k="head" cx="54" cy="' + g.cy + '" r="13" fill="' + SKIN + '" ' + W + '/>' +
    '<path data-k="nose" d="' + g.nose + '" fill="' + SKIN + '" ' + W + '/>' +
    '<path data-k="eye" d="' + g.eye + '" fill="none" stroke="' + LN + '" stroke-width="1.4" stroke-linecap="round" ' + NSS + '/>' +
    '<path data-k="hair" d="' + g.hair + '" fill="' + HAIR + '" ' + W + '/></g>';
}

/* Inch ruler beside the mattress, full height = n inches; the marker follows the mattress top. */
const RX = 355;
function ruler(n) {
  const inch = H / n;
  let ticks = '';
  for (let i = 0; i <= n; i++) ticks += 'M' + (RX - (i % 4 ? 3.5 : 6)) + ' ' + f(B - i * inch) + 'H' + RX;
  return '<g data-k="ruler"><path d="M' + RX + ' ' + B + 'V' + f(B - H - 2) + ticks + '" fill="none" stroke="' + LN + '" stroke-width="1.3" stroke-linecap="round" ' + NSS + '/>' +
    '<g data-k="rmark" transform="translate(0 ' + f(-H) + ')"><path d="M' + (X1 + 3) + ' ' + B + 'H' + (RX + 2) + '" stroke="' + HARM + '" stroke-width="2.2" stroke-linecap="round" ' + NSS + '/>' +
    '<text data-k="r8" data-fit="full" x="' + (RX + 4) + '" y="' + B + '" dominant-baseline="central" ' + FONT + ' fill="' + LN + '"></text>' +
    '<text data-k="r1" data-fit="thin" x="' + (RX + 4) + '" y="' + B + '" dominant-baseline="central" ' + FONT + ' fill="#8A3410" opacity="0"></text></g></g>';
}
/* Friendly elephant and the board, drawn for a 0-height mattress; each frame only moves the group up by the mattress height. */
const EL = (id) => '<symbol id="' + id + 'e" viewBox="-3 -3 66 52" overflow="visible"><g stroke="' + LN + '" stroke-width="2.1" stroke-linejoin="round" stroke-linecap="round" ' + NSS + '>' +
  '<path d="M7 20q-6 2-5 9" fill="none"/>' +
  '<rect x="12" y="29" width="8" height="15" rx="3.5" fill="#DCCDA9"/><rect x="33" y="29" width="8" height="15" rx="3.5" fill="#DCCDA9"/>' +
  '<ellipse cx="25" cy="24" rx="19" ry="14.5" fill="#EBDFC4"/>' +
  '<rect x="17" y="30" width="8.5" height="15" rx="3.5" fill="#EBDFC4"/><rect x="38" y="30" width="8.5" height="15" rx="3.5" fill="#EBDFC4"/>' +
  '<path d="M53 22q7 8 3 16q-1.5 3 2 3" fill="none" stroke-width="6"/><path d="M53 22q7 8 3 16q-1.5 3 2 3" fill="none" stroke="#EBDFC4" stroke-width="3"/>' +
  '<circle cx="46" cy="17" r="12" fill="#EBDFC4"/>' +
  '<path d="M40 9q-10 1-9 12q1 8 9 7q3-9 0-19z" fill="#DCCDA9"/>' +
  '<circle cx="49.5" cy="14" r="1.8" fill="' + LN + '" stroke="none"/>' +
  '<ellipse cx="50.5" cy="20.5" rx="2.6" ry="1.6" fill="#F0B9A0" stroke="none"/>' +
  '<path d="M45 22q2 2.2 4.5 1" fill="none" stroke-width="1.8"/></g></symbol>';
function press(id) {
  let s = '<g data-k="press"><rect x="' + (X0 - 4) + '" y="' + (B - 5) + '" width="' + (X1 - X0 + 8) + '" height="5" rx="2" fill="' + LN + '"/>';
  for (let i = 0; i < 5; i++) s += '<use href="#' + id + 'e" x="' + (24 + i * 62) + '" y="' + (B - 52) + '" width="62" height="48"/>';
  return s + '</g>';
}
/* Tight spiral roll: k = how much is rolled up; cx = where the roll sits. */
const R0 = 6, RT = 3, LEN = X1 - X0 - 2 * R0, R_FULL = Math.sqrt(R0 * R0 + RT * LEN / Math.PI), CX_MID = 185;
const rollMarkup = (S, thin) => '<g data-k="roll"><rect data-k="strip" x="' + X0 + '" y="' + f(B - thin) + '" width="0" height="' + f(thin) + '" rx="2" fill="' + S.top + '" ' + sw(S) + '/>' +
  '<g data-k="rollg"><circle data-k="rc" r="' + R0 + '" fill="' + S.top + '"/>' +
  '<path data-k="spiral" d="" fill="none" stroke="' + LN + '" stroke-opacity=".6" stroke-width="1.3" stroke-linecap="round" ' + NSS + '/>' +
  '<circle data-k="rc" r="' + R0 + '" fill="none" ' + sw(S) + '/></g></g>';

const refsOf = (root) => { const m = {}; root.querySelectorAll('[data-k]').forEach((n) => (m[n.dataset.k] = m[n.dataset.k] || []).push(n)); return m; };
function set(refs, k, a, v) { const l = refs[k]; if (l) for (let i = 0; i < l.length; i++) l[i].setAttribute(a, v); }

const clamp = (v) => v < 0 ? 0 : v > 1 ? 1 : v;
const seg = (x, a, b) => clamp((x - a) / (b - a));
const ease = (x) => x < .5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
const sine = (x) => (1 - Math.cos(Math.PI * x)) / 2;
const out = (x) => 1 - Math.pow(1 - x, 3);
const lerp = (a, b, k) => a + (b - a) * k;

/* Timeline, ms: 0.3–1 s the elephants step down · 1–3 s they press it to 1 inch · 3.8–4.2 s they leave · 4.2–6.2 s it rolls up and
   unrolls · 6.2–6.6 s back to full height · 6.5 s she lies down · 6.7–7.6 s it goes soft · the red marker pulses 3 times, then glows. */
const END = 10400;
const MQ = typeof matchMedia === 'function' ? matchMedia('(min-width:960px)') : null;

class SundayNever extends HTMLElement {
  connectedCallback() {
    if (this.built) return;
    this.built = true;
    const id = 'nv' + (this.dataset.uid || Math.random().toString(36).slice(2, 8)).replace(/[^a-z0-9]/gi, '').slice(-16);
    this.n = Math.max(2, parseInt(this.dataset.h, 10) || 8);
    this.inch = H / this.n;
    this.dip = 18;
    const col = /^#[0-9a-f]{6}$/i.test(this.dataset.c || '') ? this.dataset.c : VIVID.low;
    this.w = { full: this.n + '', thin: t('never.thin'), long: t('never.unit_long'), short: t('never.unit_short') };
    this.sd = this.querySelector('[data-scene="sd"]');
    this.svg = this.querySelector('[data-scene="bx"]');
    this.kg = this.querySelector('.sd-nr-kg');
    if (!this.sd || !this.svg) return;
    const V = Object.assign({}, VIVID, { low: col });
    /* Sunday room: built once, never touched again (only its labels are sized). */
    this.sd.innerHTML = '<defs><radialGradient id="' + id + 'sh"></radialGradient></defs>' + room(V) + '<g data-bed transform="' + BED + '">' + bed(V) + mattress(V, H, 0) + sleeper(V, id + 's', H, CALM) + ruler(this.n) + '</g>';
    /* Other room: built once; frames only change attributes. */
    this.svg.innerHTML = '<defs>' + EL(id) + '<radialGradient id="' + id + 'h"><stop offset="0" stop-color="#D6402B" stop-opacity=".95"/><stop offset=".45" stop-color="#E0603A" stop-opacity=".6"/><stop offset="1" stop-color="#E3992E" stop-opacity="0"/></radialGradient></defs>' +
      room(TIRED) + '<g data-bed transform="' + BED + '">' + bed(TIRED) + '<g data-k="matt">' + mattress(TIRED, H, 0) + '</g>' + press(id) + rollMarkup(TIRED, this.inch) +
      '<g data-k="sl">' + sleeper(TIRED, id, H, CALM) + '</g>' + ruler(this.n) + '</g>';
    this.refs = refsOf(this.svg);
    this.mode = '';
    this.setVB = () => [this.sd, this.svg].forEach((n) => n.setAttribute('viewBox', MQ && MQ.matches ? '0 10 360 118' : '0 12 360 116'));
    this.setVB();
    if (MQ) MQ.addEventListener('change', this.setVB);
    this.fit = () => { this.fitLabels(this.sd); this.fitLabels(this.svg); };
    this.fit();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.fit);
    if ('ResizeObserver' in window) { this.ro = new ResizeObserver(() => this.fit()); this.ro.observe(this.svg); }
    this.render(0);
    const btn = this.querySelector('[data-nr-btn]');
    if (btn) btn.addEventListener('click', (e) => { e.stopPropagation(); this.play(); });
    this.querySelectorAll('[data-nr-row]').forEach((r) => r.addEventListener('click', (e) => { if (!e.target.closest('a,button')) this.play(); }));
    const art = this.svg.parentNode;
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { this.io.disconnect(); this.io = null; this.play(); } }, { threshold: .5 });
      this.io.observe(art);
      /* pause the loop while the room is off screen, carry on where it stopped */
      this.vis = true;
      this.io2 = new IntersectionObserver((es) => { const v = es[es.length - 1].isIntersecting; if (v === this.vis) return; this.vis = v; if (v) this.resume(); else this.pause(); });
      this.io2.observe(art);
    } else this.play();
  }
  disconnectedCallback() {
    cancelAnimationFrame(this.raf);
    if (this.io) this.io.disconnect();
    if (this.io2) this.io2.disconnect();
    if (this.ro) this.ro.disconnect();
    if (MQ && this.setVB) MQ.removeEventListener('change', this.setVB);
    this.built = false;
  }
  /* Bed placement and ruler labels, per screen. Phone: bed at scale .78, labels 16 px. Desktop (960+): bed scaled up and
     centred with its ruler label, about 85% of the card, labels 17 px. Label 4 px after the marker arrow; "inch" if it fits,
     else "in" (same answer in both rooms, which share one width). */
  fitLabels(svg) {
    const w = svg.getBoundingClientRect().width;
    if (!w) return;
    const vb = svg.viewBox.baseVal.width || 360, s = w / vb, W = this.w, desk = !!(MQ && MQ.matches), px = desk ? 17 : 16;
    const labs = svg.querySelectorAll('[data-fit]');
    const place = (S, u) => { const k = s * S; let r = 0;
      labs.forEach((n) => { n.setAttribute('font-size', f(px / k)); n.setAttribute('x', f(RX + 2 + 5.1 / k)); n.textContent = W[n.dataset.fit] + ' ' + u; });
      labs.forEach((n) => { const bb = n.getBBox(); r = Math.max(r, bb.x + bb.width); }); return r; };
    let S = .78, X = 2, done = false;
    if (desk) {
      for (const S1 of [.9, .87, .84, .81, .78]) {
        for (const u of [W.long, W.short]) { const total = (place(S1, u) - 10) * S1;
          if (total <= vb - 24 / s) { S = S1; X = (vb - total) / 2 - 10 * S1; done = true; break; } }
        if (done) break;
      }
      if (!done) { S = .78; X = 2; place(S, W.short); }
    } else if (X + place(S, W.long) * S > vb - 8 / s) place(S, W.short);
    const ty = 121 - 110 * S, bedG = svg.querySelector('[data-bed]');
    if (bedG) bedG.setAttribute('transform', 'translate(' + f(X) + ' ' + f(ty) + ') scale(' + S + ')');
    this.dip = desk ? 16 : 18; /* deepest sag about 14 px at 390, about 22 px at 1300 */
    svg._g = { k: s * S, floor: (121 - ty) / S, fs: px / (s * S) };
    if (svg === this.svg && this.refs) this.render(this.tm || 0);
  }
  play() {
    cancelAnimationFrame(this.raf);
    this.render(0);
    this.t0 = performance.now();
    this.playing = true;
    this.setAttribute('data-playing', '');
    if (this.vis !== false) this.loop();
    else this.at = 0;
  }
  loop() {
    const step = (now) => {
      const tm = Math.min(END, now - this.t0);
      this.render(tm);
      if (tm < END) this.raf = requestAnimationFrame(step);
      else { this.playing = false; this.removeAttribute('data-playing'); }
    };
    this.raf = requestAnimationFrame(step);
  }
  pause() { if (!this.playing) return; cancelAnimationFrame(this.raf); this.at = performance.now() - this.t0; }
  resume() { if (!this.playing) return; this.t0 = performance.now() - (this.at || 0); this.loop(); }
  seek(tm) { cancelAnimationFrame(this.raf); this.playing = false; this.render(tm); }
  show(mode, fig) {
    const key = mode + (fig ? 1 : 0);
    if (key === this.mode) return;
    this.mode = key;
    const R = this.refs;
    set(R, 'matt', 'display', mode === 'roll' ? 'none' : 'inline');
    set(R, 'press', 'display', mode === 'press' ? 'inline' : 'none');
    set(R, 'roll', 'display', mode === 'roll' ? 'inline' : 'none');
    set(R, 'sl', 'display', fig ? 'inline' : 'none');
  }
  mattAt(h, d) { const g = mattGeo(h, d), R = this.refs; set(R, 'mf', 'd', g.o); set(R, 'mo', 'd', g.o); set(R, 'ml', 'd', g.l); set(R, 'ms', 'd', g.s); }
  figAt(h, d, dy, spine, k, p) {
    const g = figGeo(h, d, dy, p), e = g.heat, R = this.refs;
    set(R, 'heat', 'cy', e.cy); set(R, 'heat', 'rx', e.rx); set(R, 'heat', 'ry', e.ry); set(R, 'heat', 'opacity', f(k * (.55 + .3 * p) * 100) / 100);
    set(R, 'pil', 'y', g.pt); set(R, 'torso', 'd', g.torso); set(R, 'spine', 'd', g.spine); set(R, 'spine', 'stroke', spine);
    set(R, 'arm', 'd', g.arm); set(R, 'hand', 'cy', g.hand); set(R, 'duvet', 'd', g.duvet); set(R, 'hem', 'd', g.hem);
    set(R, 'neck', 'd', g.neck); set(R, 'head', 'cy', g.cy); set(R, 'nose', 'd', g.nose); set(R, 'eye', 'd', g.eye); set(R, 'hair', 'd', g.hair);
  }
  rollAt(k, cx) {
    const R = this.refs, len = k * LEN, r = Math.sqrt(R0 * R0 + RT * len / Math.PI);
    if (cx == null) cx = Math.max(X0 + r, X1 - len - R0);
    set(R, 'strip', 'width', k < 1 && cx - X0 > 2 ? f(cx - X0) : 0);
    set(R, 'rollg', 'transform', 'translate(' + f(cx) + ' ' + f(B - r) + ')');
    set(R, 'rc', 'r', f(r));
    const a0 = cx / r, turns = Math.max(.6, (r - 2.5) / RT);
    let sp = '';
    for (let i = 0; i <= 90; i++) { const u = i / 90, th = u * turns * 2 * Math.PI, rr = 2.5 + (r - 2.5) * u; sp += (i ? 'L' : 'M') + f(Math.cos(th + a0) * rr) + ' ' + f(Math.sin(th + a0) * rr); }
    set(R, 'spiral', 'd', sp);
  }
  rulerAt(h, g) {
    /* the full number only near full height, "1" only near the thin strip, nothing in between; near the floor the label
       lifts so it stays 10 px above the floor line */
    const R = this.refs, a8 = clamp((h - 29) / 6), a1 = clamp((this.inch * 1.9 - h) / 3);
    set(R, 'rmark', 'transform', 'translate(0 ' + f(-h) + ')');
    set(R, 'r8', 'opacity', f(a8 * 100) / 100);
    set(R, 'r1', 'opacity', f(a1 * 100) / 100);
    if (g) { const lift = Math.min(0, g.floor - 10 / g.k - g.fs * .66 - (B - h)); set(R, 'r1', 'y', f(B + lift)); set(R, 'r8', 'y', f(B + lift)); }
  }
  render(tm) {
    this.tm = tm;
    const R = this.refs, thin = this.inch;
    const down = sine(seg(tm, 300, 1000)), squash = sine(seg(tm, 1000, 3000)), leave = sine(seg(tm, 3800, 4200));
    const rise = sine(seg(tm, 6200, 6600)), settle = out(seg(tm, 6500, 6850)), soft = ease(seg(tm, 6700, 7600));
    let h;
    if (tm < 4200) {
      h = lerp(H, thin, squash);
      this.show('press', false);
      this.mattAt(h, 0);
      set(R, 'press', 'transform', 'translate(0 ' + f(-h - (1 - down) * 4 - leave * 70) + ')');
      set(R, 'press', 'opacity', f((1 - leave) * 100) / 100);
    } else if (tm < 6200) {
      h = thin;
      this.show('roll', false);
      const left = X0 + R_FULL;
      if (tm < 5000) this.rollAt(ease(seg(tm, 4200, 5000)));
      else if (tm < 5450) this.rollAt(1, lerp(left, CX_MID, sine(seg(tm, 5000, 5250))));
      else if (tm < 5650) this.rollAt(1, lerp(CX_MID, left, sine(seg(tm, 5450, 5650))));
      else this.rollAt(1 - ease(seg(tm, 5650, 6200)));
    } else {
      h = lerp(thin, H, rise);
      const app = tm >= 6500;
      this.show('rest', app);
      this.mattAt(h, this.dip * soft);
      const mk = sine(seg(tm, 6700, 7100)), ph = tm - 6700, pulse = ph > 0 && ph < 3600 ? (1 - Math.cos(2 * Math.PI * ph / 1200)) / 2 : 0;
      if (app) this.figAt(h, this.dip * soft, lerp(-3, 0, settle), CALM, mk, pulse);
    }
    this.rulerAt(h, this.svg._g);
    if (this.kg) this.kg.style.opacity = f(sine(seg(tm, 1200, 1800)) * (1 - sine(seg(tm, 3800, 4100))) * 100) / 100;
  }
}
if (!customElements.get('sunday-never')) customElements.define('sunday-never', SundayNever);
